import { useEffect, useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords,
  GridColumnMenuFilter
} from '@progress/kendo-react-grid';
import {
  State,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { useNavigate } from 'react-router-dom';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { GlobalLibraryCardView } from './global-library-cardview';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import { CopyGameAsync } from '../services/games';
import { appStore } from '../stores/app-store';
import { ActionCell, ActionsCell } from '../components/grid/actions-cell';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { useToast } from '../contexts/toast-context';
import { useGetGames } from '../hooks/services/use-games';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const GlobalLibraryGrid = () => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetGames(
    toDataSourceRequestString(dataState),
    'library'
  );

  const onColumnTextClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td
        title={props.dataItem.shortDescription || props.dataItem.name}
        className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {' '}
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const onAddCustomAction = (props: GridCellProps) => (
    <AddCustomAction {...props} />
  );

  const onViewLibrary = (id: number) => {
    navigate('/global-games/' + id + '?page=globallibraries');
  };

  const onCopyLibrary = async (id: number) => {
    try {
      appStore.showLoading();

      await CopyGameAsync(id, true);

      onToastOpen({
        title: 'My library',
        body: <div>Game copied to My Library.</div>,
        style: 'success'
      });
    } catch (err) {
      onToastOpen({
        title: 'My library',
        body: <div>Failed to copy to My Library.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      mutate();
    }
  };

  const AddCustomAction = (props: GridCellProps) => {
    return (
      <>
        <ActionsCell {...props}>
          <ActionCell
            title={'View game'}
            icon={'visibility'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onViewLibrary(props.dataItem.id)}
          />
          <ActionCell
            title={'Copy to my game'}
            icon={'content_copy'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onCopyLibrary(props.dataItem.id)}
          />
        </ActionsCell>
      </>
    );
  };

  const handleTitleClick = (id: number) => {
    navigate('/global-games/' + id + '?page=globallibraries');
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.createdDateUtc)}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps, field: string) => {
    return (
      <td title={props.dataItem[field]} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>{props.dataItem[field]}</div>
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'Global Games'}>
        <HelpSupport
          title="Global games library guide"
          url="https://forum.catalystglobal.com/t/4211"
        />
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search global games'}
          columnsToSearch={['name', 'mapType', 'languages', 'createdBy']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            const newState = {
              ...e.dataState,
              filter: {
                logic: 'and',
                filters: [e.dataState.filter!]
              }
            } as State;
            setDataState(newState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`libraries.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-library'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'name'}
                title={'Name'}
                columnMenu={ColumnMenu}
                cells={{
                  data: onColumnTextClick
                }}
              />
              <Column
                field={'mapType'}
                title={'Map Type'}
                width={130}
                filter={'text'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                width={110}
                cells={{
                  data: DateCell
                }}
              />
              <Column
                field={'createdBy'}
                title={'Game Designer'}
                width={175}
                columnMenu={ColumnMenu}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'createdBy')
                }}
              />
              <Column
                field={'languages'}
                title={'Languages'}
                width={200}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'languages')
                }}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              <Column
                field={'version'}
                title={'Version'}
                width={120}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'organisation'}
                title={'Organisation Name'}
                columnMenu={ColumnMenu}
                width={205}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'organisation')
                }}
              />
              <Column
                title={'Actions'}
                cells={{ data: onAddCustomAction }}
                width={120}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
          {gridMode === 'card' && (
            <GlobalLibraryCardView
              data={data?.data ?? []}
              onTitleClick={handleTitleClick}
              onView={onViewLibrary}
              onCopy={onCopyLibrary}
              total={data?.total}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>
      </div>
    </>
  );
};
export default GlobalLibraryGrid;
